'use client';

import React, { FC, useState } from 'react';
import classNames from 'classnames';
import { ChevronIcon } from '@/components';
import Heading, {
  HeadingSize,
  HeadingTag,
} from '@/components/ui/Heading/Heading';

export interface CollapsibleProps {
  title: string;
  children: React.ReactNode;
  collapsedStateOverrideValue?: boolean;
  classes?: string;
  onOpen?: () => void;
  onClose?: () => void;
  titleSupplementaryJSX?: React.ReactNode;
  hideTopDivider?: boolean;
}

const Collapsible: FC<CollapsibleProps> = ({
  title,
  children,
  collapsedStateOverrideValue,
  classes,
  onOpen,
  onClose,
  titleSupplementaryJSX,
  hideTopDivider = false,
}) => {
  const shouldTrackCollapsedStateInternally =
    collapsedStateOverrideValue === undefined;

  const defaultInternalCollapsedState = true;
  const [collapsedInternalState, toggleCollapsedInternalState] = useState(
    shouldTrackCollapsedStateInternally
      ? defaultInternalCollapsedState
      : collapsedStateOverrideValue,
  );

  const isCollapsed = shouldTrackCollapsedStateInternally
    ? collapsedInternalState
    : collapsedStateOverrideValue;

  function handleClick() {
    if (collapsedStateOverrideValue === true && onOpen) {
      onOpen();
    }

    if (collapsedStateOverrideValue === false && onClose) {
      onClose();
    }

    if (shouldTrackCollapsedStateInternally) {
      toggleCollapsedInternalState(!collapsedInternalState);
    }
  }
  return (
    <div
      className={classNames(
        'border-t border-y-charcoal-opacity-20 last:border-b',
        classes,
        {
          'border-t-0': hideTopDivider,
        },
      )}
    >
      <div
        className="my-6 flex cursor-pointer select-none items-baseline justify-between gap-3"
        onClick={handleClick}
        data-testid="collapsible-control"
      >
        {titleSupplementaryJSX && (
          <div className="h-4 w-4">{titleSupplementaryJSX}</div>
        )}

        <Heading
          text={title}
          tag={HeadingTag.H4}
          size={HeadingSize.H4}
          classes="flex-1"
          align="left"
        />
        <ChevronIcon direction={isCollapsed ? 'down' : 'up'} classes="mr-2" />
      </div>
      {!isCollapsed && children}
    </div>
  );
};

export default Collapsible;
